import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogTitle, DialogContent, TextField, Button, DialogActions, Typography, Box, IconButton, Stack, InputAdornment } from '@mui/material';
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CloseIcon from '@mui/icons-material/Close';
import { Backdrop, CircularProgress } from '@mui/material';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import { formatApiMoneyForInput, normalizeApiMoney, parseColombianMoneyInput } from '../../../utils/money';
import { sessionRequestConfig } from '../../../utils/csrf';

const API_URL = process.env.REACT_APP_API_URL;

const getId = (value) => (value && typeof value === 'object' ? value.id : value);
const mapIds = (values = []) => values.map(getId).filter(Boolean);
const hasValidId = (value) => {
  const id = getId(value);
  return id !== null && id !== undefined && id !== '' && typeof id !== 'object';
};

async function actualizarReporteDiario(valorAnterior, valorNuevo, fechaInicio) {
  try {
    const response = await axios.get(`${API_URL}/reporte_diario/`);

    const reportes = response.data;
    const reporteMensualidad = reportes.find(r => r.fecha === fechaInicio.split(' ')[0] && r.tipo === "M-I")

    if (reporteMensualidad) {
      const nuevoValor = ((parseFloat(reporteMensualidad.valor) - Number(valorAnterior)) + Number(valorNuevo)).toFixed(2);
      console.log(`Valor anterior: ${valorAnterior} | Valor nuevo: ${valorNuevo} | Total: ${nuevoValor}`);

      const responsePut = await axios.put(`${API_URL}/reporte_diario/${reporteMensualidad.id}/`, { valor: nuevoValor });
      console.log(responsePut);
    }
  } catch (error) {
    console.error("Error en la actualización del reporte:", error);
  }
}

export default function DialogModifyMensualidad({ openDialog, handleCloseDialog, data, handleUpdateData }) {
  const [desde, setDesde] = useState(null);
  const [hasta, setHasta] = useState(null);
  const [valor, setValor] = useState(null);
  const [observaciones, setObservaciones] = useState(null);
  const [loading, setLoading] = useState(false);
  const submitRef = useRef(false);

  useEffect(() => {
    setDesde(!data.renovado ? new Date(`${data.fecha_inicio}T00:00:00`) : new Date(`${data.renovaciones[data.renovaciones.length - 1].fecha_entrada}T00:00:00`));
    setHasta(!data.renovado ? new Date(`${data.fecha_final}T00:00:00`) : new Date(`${data.renovaciones[data.renovaciones.length - 1].fecha_salida}T00:00:00`));
    const apiValue = !data.renovado
      ? (data.valorTicketModificado || data.valor)
      : data.valorTicketModificado;
    setValor(apiValue == null ? '' : formatApiMoneyForInput(apiValue));
    setObservaciones(!data.renovado ? data.observaciones : data.renovaciones[data.renovaciones.length - 1].observaciones);
  }, [data]);

  const [error, setError] = useState("");

  // Estados de errores por campo
  const [fieldErrors, setFieldErrors] = useState({
    desde: false,
    hasta: false,
    valor: false,
  });

  const validateFields = () => {
    const errors = {
      desde: desde === null,
      hasta: hasta === null,
      valor: valor === null,
    };
    setFieldErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = async () => {
    if (submitRef.current || loading) return;
    if (!validateFields()) {
      setError("Por favor, complete todos los campos.");
      return;
    }
    submitRef.current = true;
    setLoading(true);
    try {
    const canonicalValue = parseColombianMoneyInput(valor);
    // ---------------------------------------------

    const ultimaRenovacionMovimiento = data.renovado && data.renovaciones.length > 0
      ? data.renovaciones[data.renovaciones.length - 1].movimiento
      : null;
    const movimientoId = getId(data.renovado ? ultimaRenovacionMovimiento : data.movimiento);
    const valorAnterior = data.renovado && data.renovaciones.length > 0
      ? (data.renovaciones[data.renovaciones.length - 1].valorTicketModificado || data.renovaciones[data.renovaciones.length - 1].valor)
      : (data.valorTicketModificado || data.valor);
    let contabilidadActualizada = false;

    let movementAdjustment = null;
    if (hasValidId(movimientoId)) {
      movementAdjustment = { movement_id: movimientoId, monto: canonicalValue };
      contabilidadActualizada = true;
    } else if (canonicalValue !== normalizeApiMoney(valorAnterior)) {
      throw new Error("Esta mensualidad historica no tiene movimiento asociado. Puedes cambiar fechas u observaciones, pero no el valor.");
    } else {
      console.warn("Mensualidad sin movimiento asociado. Se omitio ajuste de caja/reporte.", data);
    }

    // ---------------------------------------------
    
    let yearI = desde.getFullYear();
    let monthI = String(desde.getMonth() + 1).padStart(2, '0'); // Los meses van de 0 a 11
    let dayI = String(desde.getDate()).padStart(2, '0');

    let yearF = hasta.getFullYear();
    let monthF = String(hasta.getMonth() + 1).padStart(2, '0'); // Los meses van de 0 a 11
    let dayF = String(hasta.getDate()).padStart(2, '0');

    if (!data.renovado) {
      const updatedData = {
        cliente: getId(data.cliente),
        historial: getId(data.historial),
        usuario: getId(data.usuario),
        movimiento: getId(data.movimiento),
        fecha_inicio:  `${yearI}-${monthI}-${dayI}`,
        fecha_final:  `${yearF}-${monthF}-${dayF}`,
        valor: canonicalValue,
        valorTicketModificado: canonicalValue,
        observaciones: observaciones,
        cancelado: data.cancelado,
        fecha_cancelacion: data.fecha_cancelacion || null,
        renovado: data.renovado,
        renovaciones: mapIds(data.renovaciones),
        ticketsMensualidad: mapIds(data.ticketsMensualidad),
        ticketsRenovaciones: mapIds(data.ticketsRenovaciones),
        movement_adjustment: movementAdjustment,
      };
  
      try {
        const response = await axios.put(
          `${API_URL}/mensualidades/${data.id}/`,
          updatedData,
          sessionRequestConfig()
        );
        if (response.status === 200) {
          if (contabilidadActualizada) {
            actualizarReporteDiario(data.valorTicketModificado, canonicalValue, data.fecha_creacion);
          }
  
          await handleUpdateData();
          handleCloseDialog();
          setLoading(false);
        } else {
          setLoading(false);
          throw new Error("Error al actualizar los datos");
        }
      } catch (err) {
        setLoading(false);
        console.log(err)
        setError(getApiErrorMessage(
          err,
          'No fue posible actualizar la mensualidad.',
          ['renovaciones', 'historial', 'cliente', 'movimiento', 'usuario']
        ));
      }
    } else {
      const updatedDataM = {
        cliente: getId(data.cliente),
        historial: getId(data.historial),
        usuario: getId(data.usuario),
        movimiento: getId(data.movimiento),
        fecha_inicio: data.fecha_inicio,
        fecha_final: data.fecha_final,
        valor: data.valor,
        valorTicketModificado: canonicalValue,
        observaciones: data.observaciones,
        cancelado: data.cancelado,
        fecha_cancelacion: data.fecha_cancelacion || null,
        renovado: data.renovado,
        renovaciones: mapIds(data.renovaciones),
        ticketsMensualidad: mapIds(data.ticketsMensualidad),
        ticketsRenovaciones: mapIds(data.ticketsRenovaciones),
      };
  
      const ultimaRenovacion = data.renovaciones[data.renovaciones.length - 1];
      const updatedData = {
        movimiento: getId(ultimaRenovacion.movimiento),
        usuario: data.usuario.nombre,
        fecha_entrada:  `${yearI}-${monthI}-${dayI}`,
        fecha_salida:  `${yearF}-${monthF}-${dayF}`,
        valor: canonicalValue,
        valorTicketModificado: canonicalValue,
        observaciones: observaciones
      };
      updatedDataM.movement_adjustment = movementAdjustment;
      updatedDataM.renewal_update = {
        id: ultimaRenovacion.id,
        data: updatedData,
      };
  
      try {
        const responseRenovacionModificada = await axios.put(
          `${API_URL}/mensualidades/${data.id}/`,
          updatedDataM,
          sessionRequestConfig()
        );
        if (responseRenovacionModificada.status === 200) {
          if (contabilidadActualizada) {
            actualizarReporteDiario(data.renovaciones[data.renovaciones.length - 1].valor, canonicalValue, data.fecha_creacion);
          }

          setLoading(false);

          await handleUpdateData();
          handleCloseDialog();
          setLoading(false);
        } else {
          throw new Error("Error al actualizar los datos");
        }
      } catch (err) {
        setLoading(false);
        console.log(err)
        setError(getApiErrorMessage(
          err,
          'No fue posible actualizar la mensualidad.',
          ['renovaciones', 'historial', 'cliente', 'movimiento', 'usuario']
        ));
      }
    }
    } catch (err) {
      console.log(err);
      setError(getApiErrorMessage(
        err,
        'No fue posible actualizar la mensualidad.',
        ['renovaciones', 'historial', 'cliente', 'movimiento', 'usuario']
      ));
    } finally {
      submitRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog open={openDialog} onClose={handleCloseDialog}>
          <Backdrop
            open={loading}
            style={{ zIndex: 9999, color: '#fff' }}
          >
              <CircularProgress color="inherit" />
          </Backdrop>
      <DialogTitle>Modificar Mensualidad</DialogTitle>
      <DialogContent>
          <Typography variant="subtitle2" sx={{ marginTop: 2 }}>VALOR:</Typography>
          <TextField
            autoFocus
            fullWidth
            value={valor}
            type='text'
            inputMode='decimal'
            inputProps={{ maxLength: 20 }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, maxLength: 10 }}
            sx={{ textTransform: 'uppercase' }}
            onChange={(e) => { setValor(e.target.value); setFieldErrors((prev) => ({ ...prev, valor: false })); }} // Resetear el error al cambiar el valor
            error={fieldErrors.valor} // Mostrar error si el campo está vacío
            helperText={fieldErrors.valor ? "Este campo es obligatorio" : ""} // Mensaje de error
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Typography variant="subtitle2" sx={{ marginTop: 2 }}>DESDE:</Typography>
            <Stack>
              <DatePicker
                fullWidth
                variant='outlined'
                value={desde}
                onChange={(newValue) => { setDesde(newValue); setFieldErrors((prev) => ({ ...prev, desde: false })); }} // Resetear el error al cambiar la fecha
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={fieldErrors.desde} // Mostrar error si el campo está vacío
                    helperText={fieldErrors.desde ? "Este campo es obligatorio" : ""} // Mensaje de error
                  />
                )}
              />
              {fieldErrors.desde && <Typography color="error">Este campo es obligatorio</Typography>}  
            </Stack>

            <Typography variant="subtitle2" sx={{ marginTop: 2 }}>HASTA:</Typography>
            <Stack>
              <DatePicker
                fullWidth
                variant='outlined'
                value={hasta}
                onChange={(newValue) => { setHasta(newValue); setFieldErrors((prev) => ({ ...prev, hasta: false })); }} // Resetear el error al cambiar la fecha
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={fieldErrors.hasta} // Mostrar error si el campo está vacío
                    helperText={fieldErrors.hasta ? "Este campo es obligatorio" : ""} // Mensaje de error
                  />
                )}
              />
              {fieldErrors.hasta && <Typography color="error">Este campo es obligatorio</Typography>}  
            </Stack>
          </LocalizationProvider>

          <Typography variant="subtitle2" sx={{ marginTop: 2 }}>OBSERVACIONES:</Typography>
          <TextField 
              autoFocus
              fullWidth 
              value={observaciones}
              onChange={(e) => {setObservaciones(e.target.value)}}
              sx={{ textTransform: 'uppercase' }}
          />
      </DialogContent>
      {error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#d32f2f', padding: 1, borderRadius: 1, width: '95%' }}>
            <Typography sx={{ color: 'white', fontWeight: 'bold', marginRight: 1 }}>{error}</Typography>
            <IconButton color="inherit" onClick={() => setError("")}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      )}
      <DialogActions>
        <Button onClick={handleCloseDialog} variant="outlined" color='error'>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
